import { IconRendererContract } from '../IconRendererContract.js';

if (typeof window !== 'undefined') {
    import('./fontawesome.scss');
}

export const DEFAULT_FONTAWESOME_ICON_NAME_MAP = Object.freeze({

    '#refresh': 'sync',
    '#recalc': 'calculator',

    '#show': 'eye',
    '#open': 'arrow-up-right-from-square',
    '#close': 'times',
    '#back': 'arrow-left',

    '#add': 'plus',
    '#create': 'plus',
    '#edit': 'pen',
    '#edit-in-place': 'pen-to-square',
    '#save': 'floppy-disk',
    '#delete': 'trash-alt',
    '#delete-selected': 'trash-list',
    '#filter': 'filter',
    '#lock': 'lock-alt',
    '#unlock': 'lock-open-alt',
    '#preview': 'eye',

    '#accept': 'check',
    '#confirm': 'check',
    '#cancel': 'times',

    '#insert': 'plus-circle',
    '#remove': 'minus-circle',
    '#clear': 'eraser',
    '#move-up': 'chevron-circle-up',
    '#move-down': 'chevron-circle-down',
    '#move-left': 'chevron-circle-left',
    '#move-right': 'chevron-circle-right',

    '#chevron-up': 'chevron-up',
    '#chevron-down': 'chevron-down',
    '#chevron-left': 'chevron-left',
    '#chevron-right': 'chevron-right',

    '#arrow-up': 'arrow-up',
    '#arrow-down': 'arrow-down',
    '#arrow-left': 'arrow-left',
    '#arrow-right': 'arrow-right',

    '#expand': 'angle-down',
    '#collapse': 'angle-up',

    '#toggle-on': 'toggle-large-off',
    '#toggle-off': 'toggle-large-on',

    '#checked': 'square-check',
    '#unchecked': 'square',

    '#upload': 'upload',
    '#download': 'download',
    '#attachment': 'paperclip',

    '#file': 'file',
    '#file-add': 'file-plus',
    '#file-delete': 'file-minus',
    '#file-edit': 'file-edit',
    '#file-binary': 'file-binary',
    '#file-text': 'file-lines',
    '#file-image': 'file-image',
    '#file-audio': 'file-audio',
    '#file-video': 'file-video',
    '#file-archive': 'file-archive',
    '#file-code': 'file-code',
    '#file-csv': 'file-csv',
    '#file-pdf': 'file-pdf',
    '#file-word': 'file-word',
    '#file-excel': 'file-excel',
    '#file-powerpoint': 'file-powerpoint',

    '#folder': 'folder',
    '#folder-outline': 'folder',
    '#folder-open': 'folder-open',
    '#folder-add': 'folder-plus',
    '#folder-up': 'folder-upload',

    '#home': 'home',
    '#next': 'chevron-right',
    '#prior': 'chevron-left',
    '#first': 'chevrons-left',
    '#last': 'chevrons-left',

    '#user': 'user',
    '#user-group': 'users',
    '#account': 'circle-user',
    '#login': 'arrow-right-to-bracket',
    '#logout': 'arrow-right-from-bracket',

    '#play': 'play',
    '#stop': 'stop',
    '#pause': 'pause',
    '#backward': 'angle-left',
    '#fast-backward': 'angle-double-left',
    '#forward': 'angle-right',
    '#fast-forward': 'angle-double-right',

    '#search': 'magnifying-glass',

    '#sort': 'arrow-down-arrow-up',
    '#sort-up': 'arrow-up',
    '#sort-down': 'arrow-down',

    '#filter': 'filter',
    '#no-filter': 'filter-slash',

    '#layout-table': 'th-list',
    '#layout-thumbnails': 'th-large',

    '#menu': 'bars',
    '#options': 'ellipsis',
    '#options-vertical': 'ellipsis-vertical',
    '#settings': 'cog',
    '#language': 'language',

    '#zoom-in': 'search-plus',
    '#zoom-out': 'search-minus',

    '#maximize': 'arrows-maximize',
    '#minimize': 'arrows-minimize',
    '#zoom-in': 'magnifying-glass-plus',
    '#zoom-out': 'magnifying-glass-minus',

    '#error': 'triangle-exclamation',
    '#warning': 'circle-exclamation',
    '#info': 'info-circle',
    '#question': 'circle-question',

    '#pin': 'thumbtack',
    '#unpin': 'thumbtack-slash',

    '#calendar': 'calendar-alt',
    '#calendar-day': 'calendar-day',
    '#calendar-week': 'calendar-week',
    '#clock': 'clock',

    '#spinner': 'spinner',

    '#chart': 'chart-line',
    '#chart-line': 'chart-line',
    '#chart-pie': 'chart-pie',
    '#chart-bar': 'chart-bar',
    '#chart-area': 'chart-area',
    '#chart-scatter': 'chart-scatter',

    '#table': 'table',

});

const STYLE_PREFIX = Object.freeze({
    solid: 'fa-solid',
    regular: 'fa-regular',
    brands: 'fa-brands',
    light: 'fa-light',
    thin: 'fa-thin',
    duotone: 'fa-duotone',
});

export class FontawesomeIconRenderer extends IconRendererContract {

    constructor(options = {}) {

        super();

        const {
            elementRenderer = null,
            defaultStyle = 'regular',
            nameMap = {},
        } = options;

        if (!elementRenderer || typeof elementRenderer.renderElement !== 'function')
            throw new Error(`[${this.constructor.name}] "options.elementRenderer" is required.`);

        this.elementRenderer = elementRenderer;
        this.defaultStyle = defaultStyle;
        this.nameMap = {
            ...DEFAULT_FONTAWESOME_ICON_NAME_MAP,
            ...nameMap,
        };

    }

    renderIcon(name = null, options = null) {

        const actualName = this.resolveName(name);
        const actualOptions = options ?? {};
        const classes = [
            this.resolveStyle(actualOptions.style),
            actualName ? `fa-${actualName}` : null,
            actualOptions.size ? `fa-${actualOptions.size}` : null,
            actualOptions.fixedWidth ? 'fa-fw' : null,
            actualOptions.spin ? 'fa-spin' : null,
            actualOptions.pulse ? 'fa-pulse' : null,
            actualOptions.rotate ? `fa-rotate-${actualOptions.rotate}` : null,
            actualOptions.flip ? `fa-flip-${actualOptions.flip}` : null,
            actualOptions.css,
        ].filter(Boolean).join(' ');

        return this.elementRenderer.renderElement('i', classes || null, {
            ...(actualOptions.props ?? {}),
            'aria-hidden': actualOptions.props?.['aria-hidden'] ?? 'true',
        });

    }

    resolveName(name) {

        if (!name) return null;
        const normalizedName = String(name).replace(/^fa-/, '');
        return (this.nameMap[normalizedName] ?? normalizedName).replace(/^fa-/, '');

    }

    resolveStyle(style) {

        const requested = style ?? this.defaultStyle;
        return STYLE_PREFIX[requested] ?? requested ?? STYLE_PREFIX.solid;

    }

}
